import { useEffect, useState, Fragment } from "react";
import { api } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Trash2, Tag, Copy, Check, X } from "lucide-react";
import { toast } from "sonner";

const BLANK = {
  code: "",
  type: "percent",
  value: 10,
  restrict_tier_key: "",
  restrict_session_type_id: "",
  max_total_uses: "",
  notes: "",
};

function fmtMoney(cents) {
  return `$${((cents || 0) / 100).toFixed(2)}`;
}

export default function PromoCodesAdminTab() {
  const [codes, setCodes] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ...BLANK });
  const [submitting, setSubmitting] = useState(false);
  const [redemptions, setRedemptions] = useState({});
  const [copiedCode, setCopiedCode] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [c, t, s] = await Promise.all([
        api.get("/admin/promo-codes"),
        api.get("/tiers"),
        api.get("/booking/session-types"),
      ]);
      setCodes(c.data || []);
      setTiers(t.data || []);
      setSessionTypes(s.data || []);
    } catch (e) {
      toast.error("Could not load promo codes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) return toast.error("Enter a code name");
    setSubmitting(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        value: form.type === "free" ? 0 : Number(form.value) || 0,
        restrict_tier_key: form.restrict_tier_key || null,
        restrict_session_type_id: form.restrict_session_type_id || null,
        max_total_uses: form.max_total_uses ? Number(form.max_total_uses) : null,
        notes: form.notes || "",
      };
      if (form.type === "fixed") {
        // UI collects dollars; API expects cents
        payload.value = Math.round(Number(form.value) * 100);
      }
      await api.post("/admin/promo-codes", payload);
      toast.success(`Code ${payload.code} created`);
      setForm({ ...BLANK });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create code");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/promo-codes/${c.code_id}`, { active: !c.active });
      await load();
    } catch (e) {
      toast.error("Could not update code");
    }
  };

  const removeCode = async (c) => {
    if (!window.confirm(`Delete ${c.code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/promo-codes/${c.code_id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const loadRedemptions = async (c) => {
    if (redemptions[c.code_id]) {
      setRedemptions((r) => ({ ...r, [c.code_id]: null }));
      return;
    }
    try {
      const res = await api.get(`/admin/promo-codes/${c.code_id}/redemptions`);
      setRedemptions((r) => ({ ...r, [c.code_id]: res.data || [] }));
    } catch (e) {
      toast.error("Could not load usage log");
    }
  };

  const copyToClipboard = (code) => {
    try {
      navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(""), 1500);
    } catch (e) {}
  };

  const describe = (c) => {
    if (c.type === "free") return "100% off";
    if (c.type === "percent") return `${c.value}% off`;
    return `${fmtMoney(c.value)} off`;
  };

  const scope = (c) => {
    if (c.restrict_tier_key) {
      const t = tiers.find((x) => x.tier_key === c.restrict_tier_key);
      return `Tier: ${t?.name || t?.title || c.restrict_tier_key}`;
    }
    if (c.restrict_session_type_id) {
      const s = sessionTypes.find((x) => x.session_type_id === c.restrict_session_type_id);
      return `Session: ${s?.title || c.restrict_session_type_id}`;
    }
    return "Any item";
  };

  return (
    <div className="space-y-6" data-testid="promo-codes-admin-tab">
      <div>
        <h2 className="text-xl font-light">Promo codes</h2>
        <p className="text-sm text-white/55 mt-1">
          Issue percent-off, fixed-off, or 100%-free codes for tier upgrades and session bookings. 100%-off codes skip the Stripe redirect entirely and grant access instantly.
        </p>
      </div>

      {/* Create form */}
      <form onSubmit={onCreate} className="glass rounded-2xl p-5 grid gap-4 md:grid-cols-6" data-testid="promo-code-create-form">
        <div className="md:col-span-2">
          <Label>Code</Label>
          <Input
            data-testid="promo-code-input"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            placeholder="SLAVA20"
            className="uppercase"
          />
        </div>
        <div>
          <Label>Type</Label>
          <select
            data-testid="promo-code-type"
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
            className="w-full h-9 rounded-md bg-white/5 border border-white/10 px-3 text-sm"
          >
            <option value="percent">Percent off</option>
            <option value="fixed">Fixed $ off</option>
            <option value="free">100% free</option>
          </select>
        </div>
        <div>
          <Label>{form.type === "percent" ? "Percent (1-100)" : form.type === "fixed" ? "Amount off ($)" : "Value"}</Label>
          <Input
            data-testid="promo-code-value"
            type="number"
            disabled={form.type === "free"}
            value={form.type === "free" ? "" : form.value}
            onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
            placeholder={form.type === "free" ? "n/a" : form.type === "percent" ? "20" : "50"}
          />
        </div>
        <div>
          <Label>Max total uses</Label>
          <Input
            data-testid="promo-code-max-uses"
            type="number"
            value={form.max_total_uses}
            onChange={(e) => setForm((f) => ({ ...f, max_total_uses: e.target.value }))}
            placeholder="unlimited"
          />
        </div>
        <div className="md:col-span-6 grid md:grid-cols-2 gap-4">
          <div>
            <Label>Restrict to tier (optional)</Label>
            <select
              data-testid="promo-code-tier"
              value={form.restrict_tier_key}
              onChange={(e) => setForm((f) => ({ ...f, restrict_tier_key: e.target.value, restrict_session_type_id: "" }))}
              className="w-full h-9 rounded-md bg-white/5 border border-white/10 px-3 text-sm"
            >
              <option value="">— Any tier —</option>
              {tiers.filter((t) => t.active).map((t) => (
                <option key={t.tier_key} value={t.tier_key}>{t.name || t.title} ({fmtMoney(t.price_cents)})</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Restrict to session type (optional)</Label>
            <select
              data-testid="promo-code-session"
              value={form.restrict_session_type_id}
              onChange={(e) => setForm((f) => ({ ...f, restrict_session_type_id: e.target.value, restrict_tier_key: "" }))}
              className="w-full h-9 rounded-md bg-white/5 border border-white/10 px-3 text-sm"
            >
              <option value="">— Any session type —</option>
              {sessionTypes.filter((s) => s.active).map((s) => (
                <option key={s.session_type_id} value={s.session_type_id}>{s.title} ({fmtMoney(s.price_cents)})</option>
              ))}
            </select>
          </div>
        </div>
        <div className="md:col-span-6">
          <Label>Internal notes (not shown to customer)</Label>
          <Input
            data-testid="promo-code-notes"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="e.g. Press launch · Expires end of Q1"
          />
        </div>
        <div className="md:col-span-6">
          <Button
            type="submit"
            disabled={submitting}
            data-testid="promo-code-create-btn"
            className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] text-white"
          >
            {submitting ? <><Loader2 size={14} className="animate-spin mr-2" /> Creating…</> : <><Plus size={14} className="mr-2" /> Create code</>}
          </Button>
        </div>
      </form>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center p-8 text-white/50"><Loader2 className="animate-spin" /></div>
      ) : codes.length === 0 ? (
        <div className="text-sm text-white/50 italic">No promo codes yet. Create one above.</div>
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-[11px] tracking-[0.18em] uppercase text-white/40">
              <tr>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Discount</th>
                <th className="text-left px-4 py-3">Scope</th>
                <th className="text-left px-4 py-3">Uses</th>
                <th className="text-left px-4 py-3">Active</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <Fragment key={c.code_id}>
                  <tr className="border-t border-white/5" data-testid={`promo-row-${c.code}`}>
                    <td className="px-4 py-3 font-mono">
                      <div className="flex items-center gap-2">
                        <Tag size={12} className="text-[#FF8A00]" />
                        {c.code}
                        <button
                          onClick={() => copyToClipboard(c.code)}
                          className="text-white/40 hover:text-white/70"
                          data-testid={`promo-copy-${c.code}`}
                          title="Copy"
                        >
                          {copiedCode === c.code ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                        </button>
                      </div>
                      {c.notes && <div className="text-[11px] text-white/40 mt-1">{c.notes}</div>}
                    </td>
                    <td className="px-4 py-3">{describe(c)}</td>
                    <td className="px-4 py-3">{scope(c)}</td>
                    <td className="px-4 py-3">
                      {c.used_count || 0}{c.max_total_uses ? ` / ${c.max_total_uses}` : ""}
                      <button
                        onClick={() => loadRedemptions(c)}
                        className="ml-2 text-[11px] text-[#FF8A00] hover:underline"
                        data-testid={`promo-usage-${c.code}`}
                      >
                        {redemptions[c.code_id] ? "hide" : "view log"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <Switch
                        checked={!!c.active}
                        onCheckedChange={() => toggleActive(c)}
                        data-testid={`promo-toggle-${c.code}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => removeCode(c)}
                        className="text-white/40 hover:text-red-400"
                        data-testid={`promo-delete-${c.code}`}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                  {redemptions[c.code_id] && (
                    <tr className="border-t border-white/5 bg-white/[0.02]">
                      <td colSpan={6} className="px-4 py-3">
                        {redemptions[c.code_id].length === 0 ? (
                          <div className="text-[11px] text-white/40 italic">No redemptions yet.</div>
                        ) : (
                          <div className="space-y-1 text-[12px]">
                            {redemptions[c.code_id].map((r) => (
                              <div key={r.redemption_id} className="flex justify-between gap-4 text-white/60">
                                <span>{new Date(r.created_at).toLocaleString()}</span>
                                <span>{r.user_email || "guest"}</span>
                                <span>{r.item_title}</span>
                                <span>{fmtMoney(r.original_cents)} → {fmtMoney(r.final_cents)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
